/**
 * The screens' numbers come from @map-my-money/views, the same results the MCP tools return. This
 * file only adapts them to the app's types; no number on screen is computed anywhere else.
 */
import { formatDay, goals as goalsNs, type Day } from '@map-my-money/engine';
import {
  checkInAnchors, checkpointsFrom as viewsCheckpoints, datedFieldsOf, debtPathOf, debtsWithRateChanges, decode, forecastView as viewsForecast, goalsView,
  historyLines, itemOn, largestIncome, linePoints, matchTerms, num, overview as viewsOverview, planFacts, plannedAssets, rateChangesAffectForecast,
  rateSchedule, screens, TAX_ACCOUNTS, taxReliefDefault, valuesOn, withDatedEdits, withoutChange,
  type DatedField, type ForecastOptions, type ForecastResult, type GoalsResult, type HistoryLine, type ItemFacts, type MatchTerms, type OverviewResult,
  type PlannedAsset, type RateScheduleView,
} from '@map-my-money/views';
import type { Waypoint, WirePlan } from '../api/types.ts';

export type CashflowYear = screens.CashflowYear;
export type ChartRow = screens.ChartRow;
export type CurrentState = screens.CurrentState;
export type ForecastView = screens.ForecastView;
export type Milestone = screens.Milestone;
export type MonthPoint = screens.MonthPoint;
export type MonthView = screens.MonthView;
export type OwnedAsset = screens.OwnedAsset;
export type SeriesKey = screens.SeriesKey;

export type GoalWhen = screens.GoalWhen;
export type { DatedField, HistoryLine, ItemFacts, MatchTerms, PlannedAsset, RateScheduleView };

export {
  checkInAnchors, datedFieldsOf, debtPathOf, debtsWithRateChanges, decode, historyLines, itemOn, largestIncome, linePoints, matchTerms, num, planFacts,
  plannedAssets, rateChangesAffectForecast, rateSchedule, TAX_ACCOUNTS, taxReliefDefault, valuesOn, withDatedEdits, withoutChange,
};

export const {
  currentFromOverview, decimalString, goalWhen, latest, loanPayment, monthlyTotals, monthView, ownedAssets, restOfMonthFromOverview,
  snapshotNetWorth, waypointBalances, yearsAndMonths,
} = screens;

export const checkpointsFrom = viewsCheckpoints;

export const forecastView = (wire: WirePlan, waypoints: readonly Waypoint[], today: Day, years: number): screens.ForecastView =>
  screens.forecastScreen(wire, waypoints, formatDay(today), years);

export const overview = (wire: WirePlan, waypoints: readonly Waypoint[], today: Day): OverviewResult =>
  viewsOverview({ plan: wire, waypoints, asOf: formatDay(today) });

/** get_forecast's result for the same plan, waypoints, day and options. */
export const forecastResult = (wire: WirePlan, waypoints: readonly Waypoint[], today: Day, options: ForecastOptions): ForecastResult =>
  viewsForecast({ plan: wire, waypoints, asOf: formatDay(today) }, options);

export const goals = (wire: WirePlan, waypoints: readonly Waypoint[], today: Day, years?: number): GoalsResult =>
  goalsView({ plan: wire, waypoints, asOf: formatDay(today) }, years);

export { formatDay, goalsNs };
